<template>
  <div class="movie-hat">
    <AppHeader/>
    <UpdateAvailableBanner/>
    <div
      v-if="$store.state.appError"
      class="app-error alert alert-warning alert-dismissible mx-3 my-2"
      role="alert"
    >
      {{ $store.state.appError }}
      <button
        type="button"
        class="btn-close"
        aria-label="Dismiss"
        @click="$store.commit('setAppError', null)"
      />
    </div>
    <Login v-if="!loggedIn"/>
    <div v-else class="content">
      <router-view></router-view>
    </div>
    <!-- Outside the login gate on purpose: the report that prompted this
         button was a sign-in failure, which can only be reported from the
         sign-in screen. -->
    <BugReportButton/>
    <BugResolutionNotice/>
  </div>
</template>

<script>
import Login from "./components/Login.vue";
// Registered as AppHeader: "Header" is a reserved HTML element name.
import AppHeader from "./components/Header.vue";
import UpdateAvailableBanner from "./components/UpdateAvailableBanner.vue";
import BugReportButton from "./components/BugReportButton.vue";
import BugResolutionNotice from "./components/BugResolutionNotice.vue";
import { reloadForUpdate, isSafeMomentForReload, shouldAutoAttempt, markUpdateLanded, checkWorkerOnce, runUpdateCheck } from "./utils/appUpdate.js";
import { flushStashedBugReports } from "./utils/bugReports.js";
import { refreshSubscriptionIfGranted, clearBadgeOnOpen } from "./utils/push.js";
import { repairPrecacheIfEmptied } from "./utils/precacheRepair.js";

export default {
  name: 'Movie-Hat',
  components: {
    AppHeader,
    Login,
    UpdateAvailableBanner,
    BugReportButton,
    BugResolutionNotice
  },
  data () {
    return {
      lastActivityAt: Date.now(),
      lastBecameVisibleAt: Date.now(),
      deployedBundleSeen: null,
      autoUpdateTimer: null
    };
  },
  watch: {
    // Auto-apply updates: immediately when spotted at a fresh moment (just
    // launched or foregrounded — nothing is in progress yet), otherwise
    // after a quiet stretch — and never while typing, mid-modal, during the
    // draw reveal, with a bug report half-written, or on a screen that only
    // exists in memory. Cinema Roll's pattern, both of its lessons included.
    '$store.state.updateAvailable' (available) {
      if (!available) return;
      this.armAutoUpdate();
    },
    // Push subscription self-heal: once a session exists, re-save this
    // device's subscription (repairs rotated endpoints; no-op for devices
    // that never opted in — it never prompts).
    '$store.state.email' (email) {
      if (!email) return;
      refreshSubscriptionIfGranted();
      clearBadgeOnOpen();
    }
  },
  computed: {
    loggedIn () {
      // Was: an email remembered in localStorage, which is not a session at
      // all. With the database open that worked; with it closed the app
      // rendered as signed in, sent every request without a token and showed
      // an empty screen (outage, 2026-08-17).
      //
      // `authResolved` guards the first moment: Firebase restores a session
      // asynchronously, so until it has answered we keep showing whatever the
      // remembered email implies rather than flashing the sign-in button at
      // somebody who is signed in.
      if (!this.$store.state.authResolved) {
        return this.$store.state.email;
      }
      return Boolean(this.$store.state.authUser);
    }
  },
  methods: {
    async checkForServiceWorkerUpdate () {
      // An update already spotted but not applied (deferred on a weak
      // connection, or the first try didn't land): coming back to the app is
      // a fresh moment, so give it its second automatic try.
      if (this.$store.state.updateAvailable && !this.$store.state.updateApplying && !this.autoUpdateTimer) {
        this.armAutoUpdate();
      }

      // Side by side, the worker's half capped (appUpdate.js runUpdateCheck):
      // a slow or failed worker check must never hold up spotting a deploy.
      await runUpdateCheck({
        refreshWorker: async () => {
          if (!('serviceWorker' in navigator)) return;
          const registration = await navigator.serviceWorker.getRegistration();
          // Shared with the refresh that may follow (checkWorkerOnce), so it
          // joins this check instead of queueing a second one behind it.
          if (registration) await checkWorkerOnce(registration);
        },
        checkBundle: () => this.checkDeployedBundle()
      });
    },
    /**
     * Notices a new deploy by comparing bundle filenames, independent of
     * any service worker state. This app builds with `skipWaiting: true`,
     * so a new worker activates itself immediately instead of waiting —
     * which makes registerServiceWorker's `updated()` hook a race this
     * check often wins where the hook loses (Cinema Roll bug report: "I
     * didn't get my refresh for new version banner").
     *
     * The cache-busting param matters: Workbox precaches index.html and
     * only strips params it is configured to ignore, so an arbitrary param
     * misses the precache and goes to the network — which is the point.
     */
    async checkDeployedBundle () {
      if (this.$store.state.updateAvailable) {
        return;
      }

      const runningBundle = this.currentBundleName();
      if (!runningBundle) {
        return;
      }

      try {
        const response = await fetch(`${process.env.BASE_URL || '/'}index.html?updateCheck=${Date.now()}`, { cache: 'no-store' });
        if (!response.ok) {
          return;
        }
        const deployedBundle = (await response.text()).match(/js\/app\.[a-z0-9]+\.js/);
        if (deployedBundle && deployedBundle[0] !== runningBundle) {
          this.deployedBundleSeen = deployedBundle[0];
          this.$store.commit('setUpdateTargetBundle', deployedBundle[0]);
          this.$store.commit('setUpdateAvailable', true);
        } else if (deployedBundle) {
          // Running what's deployed: any earlier reload attempt worked, so
          // the next update starts from a clean slate (appUpdate.js).
          markUpdateLanded();
        }
      } catch {
        // Offline, blocked, or the check simply failed — next trigger.
      }
    },
    /** The app bundle THIS page is running, read off its own script tags. */
    currentBundleName () {
      const scripts = Array.from(document.querySelectorAll('script[src]'));
      for (const script of scripts) {
        const match = (script.getAttribute('src') || '').match(/js\/app\.[a-z0-9]+\.js/);
        if (match) return match[0];
      }
      return null;
    },
    noteActivity () {
      this.lastActivityAt = Date.now();
    },
    async applyUpdate () {
      // The banner reads this to say "Updating…" instead of offering a
      // Refresh that would only get in the way.
      this.$store.commit('setUpdateApplying', true);
      const outcome = await reloadForUpdate({ target: this.deployedBundleSeen });
      if (outcome === 'deferred') {
        this.$store.commit('setUpdateApplying', false);
        this.$store.commit('setUpdateDeferred', true);
      }
    },
    armAutoUpdate () {
      const target = this.deployedBundleSeen || 'unknown';
      if (!shouldAutoAttempt(target)) return; // twice per version; then the banner

      // Fresh moment (just launched or just foregrounded): nothing is in
      // flight yet — apply right away.
      const fresh = Date.now() - (this.lastBecameVisibleAt || 0) < 5000;
      if (fresh && isSafeMomentForReload({ routePath: this.$route?.path || '' })) {
        this.applyUpdate();
        return;
      }

      // Otherwise: poll for a quiet stretch.
      if (this.autoUpdateTimer) clearInterval(this.autoUpdateTimer);
      this.autoUpdateTimer = setInterval(() => {
        const quiet = Date.now() - this.lastActivityAt > 25000;
        if (quiet && isSafeMomentForReload({ routePath: this.$route?.path || '' })) {
          clearInterval(this.autoUpdateTimer);
          this.autoUpdateTimer = null;
          this.applyUpdate();
        }
      }, 5000);
    }
  },
  mounted () {
    // The email watcher above misses the normal relaunch (email is restored
    // from localStorage at store init, before this component exists), and a
    // relaunch is exactly when a badge needs clearing — the user is looking
    // at the app the badge was pointing them to. Both calls are no-ops for
    // devices that never opted in.
    if (this.$store.state.email) {
      refreshSubscriptionIfGranted();
      clearBadgeOnOpen();
    }

    // visibilitychange alone is unreliable on iOS, particularly for a
    // home-screen-installed PWA — it sometimes just doesn't fire when the
    // app comes back to the foreground. pageshow and focus are more
    // consistent there, and the interval is a backstop that doesn't depend
    // on any lifecycle event at all. Between them, an update gets noticed
    // even if any single trigger fails. (Cinema Roll's hard-won set.)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.lastBecameVisibleAt = Date.now();
        this.checkForServiceWorkerUpdate();
        // Foregrounding an installed PWA doesn't remount this component, and
        // it's the most common way a badge gets looked at.
        clearBadgeOnOpen();
      }
    });
    window.addEventListener('pageshow', () => {
      this.lastBecameVisibleAt = Date.now();
      this.checkForServiceWorkerUpdate();
    });
    window.addEventListener('focus', () => {
      this.checkForServiceWorkerUpdate();
    });
    // Activity signals for the quiet-moment detector. Passive: these must
    // never delay scrolling.
    ['pointerdown', 'keydown', 'wheel', 'touchstart', 'scroll'].forEach((eventName) => {
      window.addEventListener(eventName, this.noteActivity, { passive: true });
    });
    setInterval(() => {
      this.checkForServiceWorkerUpdate();
    }, 30 * 60 * 1000);

    // Bug reports typed with no signal go out on the next launch.
    flushStashedBugReports().catch(() => {});

    // A hard reload empties the phone's saved copy of the app and nothing
    // else refills it (utils/precacheRepair.js) - put it back, well after
    // launch so it never competes with the hat loading.
    setTimeout(() => {
      repairPrecacheIfEmptied({ runningBundle: this.currentBundleName() });
    }, 15000);
  },
}
</script>

<style>
:root {
  /* Every safe-area inset in this app goes through these, so one measurement
     can adjust them in one place. Requires viewport-fit=cover in index.html —
     without it iOS reports 0 here. */
  --safe-top: env(safe-area-inset-top, 0px);

  /* iOS 26+ paints a Liquid Glass band over the top edge, and the safe-area
     inset does NOT grow to cover it. Measured off a screenshot on Matt's
     iPhone 16 Pro (402x874 CSS, 2.946 image px per pt): the band's influence
     ends 95.7pt down, while env(safe-area-inset-top) reports 59pt - so
     padding by the inset alone still left the header 37pt inside the band,
     which is exactly what he saw. Inset + feather + a few points so it reads
     as a deliberate gap rather than a near miss. Re-measure if Apple retunes
     it; that is one screenshot and tools-style column scan, not a guess. */
  --band-top: calc(var(--safe-top) + 30px);
  --safe-bottom: env(safe-area-inset-bottom, 0px);
}

body.body {
  background-color: #6ba2dc;
}

/* On a desktop the app used to stretch edge to edge — a 2000px-wide search
   box and a marquee like an aircraft carrier. One centered column keeps the
   phone layout intact (max-width is a no-op there) and makes every view
   sane on a big screen. */
.movie-hat {
  max-width: 1200px;
  margin: 0 auto;
}
</style>
